<template>
  <div class="judgment-home">
    <JpIcons />

    <!-- 上传输入区 -->
    <section class="page-wrap">
      <h1 class="page-title">裁判文书</h1>
      <div class="page-subtitle">
        上传案件材料，AI
        将自动解析案件信息、核验材料完整性，并生成对应类型的专业裁判文书。
      </div>

      <div
        class="card composer"
        :class="{ dragover: composerDragOver }"
        @dragenter="onDragEnter"
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        @drop="onDrop"
      >
        <div v-if="files.length" class="composer-files">
          <div v-for="(f, i) in files" :key="f.name" class="attachment">
            <div class="attachment-ico">
              <FileThumb :name="f.name" />
            </div>
            <div class="attachment-main">
              <div class="attachment-name">{{ f.name }}</div>
              <div class="attachment-meta">
                {{ fileExt(f.name) }} · {{ f.size }} · 已选择
              </div>
            </div>
            <button class="icon-btn" @click="removeCaseFile(i)">
              <svg class="icon sm"><use href="#jp-x" /></svg>
            </button>
          </div>
        </div>

        <textarea
          v-model="requirementText"
          class="composer-textarea"
          :placeholder="`可补充说明案件情况、裁判思路或其他文书生成要求。
支持 ZIP / RAR / 7Z 压缩包，单文件 ≤ 50MB，请将案件材料打包后上传。`"
        ></textarea>

        <div class="composer-footer">
          <div class="composer-actions">
            <button class="btn" @click="pickCaseFiles">
              <svg class="icon sm"><use href="#jp-upload" /></svg>加密上传材料
            </button>
            <button class="btn example-btn" @click="useExampleMaterial">
              <svg class="icon sm"><use href="#jp-file" /></svg>示例文书
            </button>
            <!-- 文书类型下拉 -->
            <div ref="docTypeControlRef" class="doc-type-control">
              <button
                type="button"
                class="doc-type-trigger"
                :class="{ open: docTypeMenuOpen }"
                aria-haspopup="listbox"
                :aria-expanded="docTypeMenuOpen"
                @click.stop="docTypeMenuOpen = !docTypeMenuOpen"
              >
                <span>{{ activeType }}</span>
                <svg class="icon sm doc-type-caret">
                  <use href="#jp-caret" />
                </svg>
              </button>
              <div
                class="doc-type-menu"
                :class="{ show: docTypeMenuOpen }"
                role="listbox"
              >
                <button
                  v-for="type in DOC_TYPES"
                  :key="type"
                  type="button"
                  class="doc-type-option"
                  :class="{
                    active: type === activeType,
                    disabled: type !== '判决书',
                  }"
                  role="option"
                  :aria-selected="type === activeType"
                  :aria-disabled="type !== '判决书'"
                  :disabled="type !== '判决书'"
                  @click.stop="selectDocType(type)"
                >
                  <svg class="icon option-icon"><use href="#jp-file" /></svg>
                  <span class="option-label">{{ type }}</span>
                  <svg class="icon option-check"><use href="#jp-check" /></svg>
                </button>
              </div>
            </div>
          </div>
          <div class="start-wrap">
            <span class="start-tip">{{
              !files.length
                ? "请先上传案件材料"
                : sessionUsable
                  ? "材料已解析完成，无需重新上传"
                  : "材料已就绪，可开始解析"
            }}</span>
            <button
              class="btn primary"
              :disabled="!files.length"
              @click="startParsing"
            >
              <svg class="icon sm"><use href="#jp-send" /></svg
              >{{ sessionUsable ? continueLabel : "开始解析" }}
            </button>
          </div>
        </div>

        <input
          ref="caseFileInputRef"
          type="file"
          multiple
          accept=".zip,.rar,.7z"
          hidden
          @change="handleCaseFiles"
        />
      </div>

      <!-- 最近文书 -->
      <div class="section-headline">
        <div>
          <h2>最近文书</h2>
          <div class="section-sub">
            查看近期裁判文书生成任务、资料状态与生成结果
          </div>
        </div>
        <button class="all-record-link" @click="showHistory">
          查看全部记录 <svg class="icon sm"><use href="#jp-caret" /></svg>
        </button>
      </div>

      <TypeTabs v-model="recentTabType" class="home-tabs" />

      <div class="card record-card">
        <div v-if="recentLoading" class="records-loading">
          <a-spin />
        </div>
        <div v-else-if="!recentTasks.length" class="records-empty">
          <a-empty />
        </div>
        <TaskRow
          v-for="t in recentTasks"
          :key="t.id"
          :task="t"
          @view="viewTask"
          @download="downloadTask"
          @delete="deleteTask"
          @missing="infoTask = $event"
        />
      </div>
    </section>

    <!-- 任务进度 / 缺失资料弹窗 -->
    <TaskInfoModal
      :task="infoTask"
      @close="infoTask = null"
      @supplement="goSupplementFromInfo"
    />
    <!-- 补充上传弹窗 -->
    <SupplementModal
      :task="suppTask"
      @close="suppTask = null"
      @confirm="onSuppConfirm"
    />
    <!-- 案件材料解析进度（不可关闭） -->
    <ParseProgressModal :open="parseActive" :pct="parsePct" :desc="parseDesc" />
    <!-- 解析完成·案件信息确认（提交后才创建文书记录） -->
    <CaseInfoModal
      :open="caseInfoOpen"
      :config="dossierConfig"
      @close="caseInfoOpen = false"
      @submit="submitCaseGenerate"
    />
    <!-- 补充材料流程（useTaskActions 驱动）：解析进度 + 案件信息确认后重新生成 -->
    <ParseProgressModal
      :open="suppParseActive"
      :pct="suppParsePct"
      :desc="suppParseDesc"
    />
    <CaseInfoModal
      :open="suppCaseInfoOpen"
      :config="suppConfig"
      @close="suppCaseInfoOpen = false"
      @submit="submitSuppGenerate"
    />
  </div>
</template>

<script setup>
defineOptions({ name: "JudicativePaper" });
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  onActivated,
  onDeactivated,
} from "vue";
import { useRouter } from "vue-router";
import { message } from "ant-design-vue";
import {
  DOC_TYPES,
  ARCHIVE_EXTS,
  activeType,
  activeHistoryType,
  fileExt,
  formatSize,
  mapPageItem,
  unwrapPage,
  fetchDocTypes,
  docTypeValue,
} from "./store";
import { useTaskActions } from "./useTaskActions";
import {
  importDossier,
  getGenerationConfig,
  generationJudgment,
  getJudgmentPage,
} from "@/api/judicativePaper";
import FileHandler from "@/utils/fileHandler";
import JpIcons from "./components/JpIcons.vue";
import FileThumb from "./components/FileThumb.vue";
import TypeTabs from "./components/TypeTabs.vue";
import TaskRow from "./components/TaskRow.vue";
import TaskInfoModal from "./components/TaskInfoModal.vue";
import SupplementModal from "./components/SupplementModal.vue";
import ParseProgressModal from "./components/ParseProgressModal.vue";
import CaseInfoModal from "./components/CaseInfoModal.vue";

const router = useRouter();
// 删除成功后回调 fetchRecentTasks 刷新最近文书（函数声明有提升，可前置引用）
const {
  infoTask,
  suppTask,
  suppParseActive,
  suppParsePct,
  suppParseDesc,
  suppCaseInfoOpen,
  suppConfig,
  viewTask,
  downloadTask,
  deleteTask,
  goSupplementFromInfo,
  openMissingFromGenerateError,
  isGenerateMissingError,
  onSuppConfirm,
  submitSuppGenerate,
} = useTaskActions(fetchRecentTasks);

// ---------- 最近文书（真实接口 GetPage：默认第 1 页 5 条，按当前文书类型过滤） ----------
// 记录映射 mapPageItem / 返回解包 unwrapPage 与全部记录页共用（store 导出）
// recentTabType：仅驱动「最近文书」列表筛选，与上方下拉 activeType（生成文书类型）互不联动
const recentTabType = ref("判决书");
const recentTasks = ref([]);
const recentLoading = ref(false);

// 串行守卫：快速连续切换类型时只认最后一次请求的返回（与列表页 fetchHistoryPage 同套路）
let recentSeq = 0;
// silent=true 静默刷新：不置列表 loading（生成完成后后台刷新，避免整列表闪转圈）
async function fetchRecentTasks(silent = false) {
  const seq = ++recentSeq;
  if (!silent) recentLoading.value = true;
  try {
    const res = await getJudgmentPage({
      pageIndex: 1,
      pageSize: 5,
      // documentType：最近文书 tab 选中类型的枚举 value
      documentType: docTypeValue(recentTabType.value),
    });
    if (seq !== recentSeq) return;
    recentTasks.value = unwrapPage(res).list.map(mapPageItem);
  } catch (err) {
    if (seq !== recentSeq) return;
    // 业务 / 状态码错误 http.js 已 toast；此处兜底网络类静默失败
    console.error("[JudicativePaper] getJudgmentPage error:", err);
    message.error("最近文书加载失败，请稍后重试");
  } finally {
    // 过期请求不收 loading：更晚发出的请求仍在飞行中
    if (seq === recentSeq) recentLoading.value = false;
  }
}

// 最近文书 tab 切换：按新类型重新查列表（与上方生成类型下拉无关）
watch(recentTabType, () => {
  fetchRecentTasks();
});

// 先等文书类型枚举就绪再拉列表：行内 type 按 documentType 反查文案依赖 docTypeItems
// （fetchDocTypes 失败也会 resolve 并保留默认枚举，不阻断列表加载）
onMounted(() => {
  fetchDocTypes().then(() => {
    if (!DOC_TYPES.value.includes(recentTabType.value)) {
      // 改写会触发 watch → fetchRecentTasks，此处不再重复拉取
      recentTabType.value = DOC_TYPES.value[0] || "判决书";
      return;
    }
    fetchRecentTasks();
  });
});
// 若页面被 keep-alive 缓存：首次激活已随 onMounted 加载，此后每次切回刷新
let recentActivatedOnce = false;
onActivated(() => {
  if (recentActivatedOnce) fetchRecentTasks();
  recentActivatedOnce = true;
});

function showHistory() {
  activeHistoryType.value = recentTabType.value;
  router.push("/home/JudicativePaper/list");
}

// ---------- 上传与任务创建 ----------
const files = ref([]);
const requirementText = ref("");
const caseFileInputRef = ref(null);

function pickCaseFiles() {
  caseFileInputRef.value?.click();
}
// 示例文书：public 下的静态压缩包（BASE_URL 为 publicPath，encodeURI 处理中文文件名）。
// 点击后下载到本地，供用户参考所需案件资料清单，不自动填入上传区
const EXAMPLE_MATERIAL_NAME = "民事判决书示例文件.zip";
const EXAMPLE_MATERIAL_URL = encodeURI(
  `${process.env.BASE_URL}${EXAMPLE_MATERIAL_NAME}`,
);
async function useExampleMaterial() {
  try {
    const resp = await fetch(EXAMPLE_MATERIAL_URL);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    FileHandler.saveFile(blob, EXAMPLE_MATERIAL_NAME);
    message.success("示例文件已开始下载，请参考所需资料后打包上传");
  } catch (err) {
    console.error("[JudicativePaper] download example material error:", err);
    message.error(
      `示例文件下载失败，请确认 public 下已放置 ${EXAMPLE_MATERIAL_NAME}`,
    );
  }
}
// 接收文件列表：校验后加入待上传列表（点击选择与拖拽共用）
function addCaseFiles(fileList) {
  [...fileList].forEach((f) => {
    const ext = fileExt(f.name).toLowerCase();
    if (!ARCHIVE_EXTS.includes(ext)) {
      message.warning(`仅支持上传压缩包（ZIP / RAR / 7Z）：${f.name}`);
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      message.warning(`${f.name} 超过 50MB`);
      return;
    }
    if (!files.value.some((x) => x.name === f.name)) {
      // 保留 File 对象：选择阶段不上传，「开始解析」时统一走 importDossier
      files.value.push({ name: f.name, size: formatSize(f.size), raw: f });
    }
  });
}
function handleCaseFiles(e) {
  addCaseFiles(e.target.files);
  e.target.value = "";
}

// ---------- 拖拽上传（整个 composer 卡片为放置区） ----------
// dragenter/leave 会在子元素间连续触发，用计数器避免高亮闪烁
const composerDragOver = ref(false);
let dragDepth = 0;
// 仅响应文件拖拽（忽略页内文本选区等拖动）
const dragHasFiles = (e) => e.dataTransfer?.types?.includes("Files");
function onDragEnter(e) {
  if (!dragHasFiles(e)) return;
  e.preventDefault();
  dragDepth++;
  composerDragOver.value = true;
}
function onDragOver(e) {
  if (!dragHasFiles(e)) return;
  e.preventDefault(); // 必须 preventDefault 才允许 drop
}
function onDragLeave() {
  dragDepth = Math.max(0, dragDepth - 1);
  if (!dragDepth) composerDragOver.value = false;
}
function onDrop(e) {
  e.preventDefault();
  dragDepth = 0;
  composerDragOver.value = false;
  addCaseFiles(e.dataTransfer.files);
}
function removeCaseFile(i) {
  files.value.splice(i, 1);
}

// 文书类型下拉
const docTypeMenuOpen = ref(false);
const docTypeControlRef = ref(null);
function selectDocType(type) {
  // 目前仅开放判决书生成，其余类型展示但不可选
  if (type !== "判决书") return;
  activeType.value = type;
  docTypeMenuOpen.value = false;
}
// 点击下拉外区域关闭菜单
const onDocClick = (e) => {
  if (!docTypeControlRef.value?.contains(e.target))
    docTypeMenuOpen.value = false;
};
onMounted(() => document.addEventListener("click", onDocClick));
onUnmounted(() => document.removeEventListener("click", onDocClick));

// 路由切走（keep-alive 停用）时复位：从菜单再回来落到干净的首页，
// 不保留已选压缩包与补充说明。任务列表数据在 store（模块级），不受影响；
// 文书类型 activeType 为首页/列表页共享的记忆位，刻意保留不重置。
onDeactivated(() => {
  files.value = [];
  requirementText.value = "";
  docIds.value = [];
  dossierConfig.value = null;
  parsedSession.value = null;
  // 解析流程中途切走：终止模拟定时器并收起弹窗（startParsing 靠 parseActive 判断放弃后续），
  // 回来落在干净首页
  parseTimers.forEach(clearTimeout);
  parseTimers = [];
  parseActive.value = false;
  caseInfoOpen.value = false;
});

// ---------- 解析流程 ----------
// 「开始解析」是一次流程的真正起点：此处才真实上传（importDossier），
// 上传完成进入解析动画，完成后打开案件信息确认（提交生成才创建记录）
const parseActive = ref(false);
const parsePct = ref(26);
const parseDesc = ref("正在解压案件材料");
const docIds = ref([]); // ImportDossier 返回的 documentId 列表，后续 GenerationJudgment 生成接口需要
const dossierConfig = ref(null); // GetGenerationConfig 返回的文书生成配置，预填案件信息表单
const caseInfoOpen = ref(false);
let parseTimers = [];

// ---------- 已解析会话（弹窗误关不重传） ----------
// 上传+配置成功后缓存结果；弹窗关闭后再点「开始解析」，若材料指纹未变
// 则跳过 ImportDossier / GetGenerationConfig 直接重开案件信息弹窗。
// 仅在材料增删、提交生成、路由切走时失效
const parsedSession = ref(null);
// 当前文件列表指纹（name+size+lastModified，排序后拼接，与选择顺序无关）
function filesFingerprint() {
  return files.value
    .filter((f) => f.raw)
    .map((f) => `${f.raw.name}|${f.raw.size}|${f.raw.lastModified}`)
    .sort()
    .join(";");
}
// 会话是否仍有效（材料未变化）：驱动「开始解析」按钮切换为「继续」态
const sessionUsable = computed(
  () =>
    !!parsedSession.value &&
    parsedSession.value.snapshot === filesFingerprint(),
);
const continueLabel = "继续填写案件信息";

async function startParsing() {
  if (!files.value.length || parseActive.value) return;
  // 有效会话：材料未变化，免重传 / 免重拉配置 / 免解析动画，直接重开案件信息弹窗
  const session = parsedSession.value;
  if (session && session.snapshot === filesFingerprint()) {
    docIds.value = session.docIds;
    dossierConfig.value = session.config;
    caseInfoOpen.value = true;
    return;
  }
  // 材料已变化：旧会话作废，走完整上传流程
  parsedSession.value = null;
  parseActive.value = true;
  parsePct.value = 26;
  parseDesc.value = "正在上传案件材料…";
  const startedAt = Date.now();
  const snapshot = filesFingerprint(); // 解析期间进度弹窗阻塞交互，指纹不会再变
  try {
    // 真实上传：输入区所有材料（手动选择的压缩包），
    // documentType 带上当前选中文书类型的枚举 value
    const raws = files.value.filter((f) => f.raw);
    const documentType = docTypeValue(activeType.value);
    const results = raws.length
      ? await Promise.all(raws.map((f) => importDossier(f.raw, documentType)))
      : [];
    // 返回外层可能是 { code, data } 或平铺（与 ContractReview 的取值约定一致）
    const unwrapped = results.map((res) => (res && (res.data || res)) || {});
    docIds.value = unwrapped.map((res) => res.documentId ?? res.docId ?? null);
    // 路由已切走（onDeactivated 收起了弹窗并复位状态）：终止流程
    if (!parseActive.value) return;
    // 上传完成 → 用返回的 documentId 调 GetGenerationConfig 拿文书生成配置，
    // 作案件信息表单预填数据（多文件取首个 documentId）。
    // 配置拉取失败不阻断解析流程：表单回退默认预填值（业务错误 http.js 已 toast）
    const primaryDocId = docIds.value.find(Boolean);
    if (primaryDocId) {
      try {
        const cfgRes = await getGenerationConfig(primaryDocId);
        dossierConfig.value = (cfgRes && (cfgRes.data || cfgRes)) || null;
      } catch (err) {
        console.error("[JudicativePaper] getGenerationConfig error:", err);
      }
      if (!parseActive.value) return;
    }
    // 上传完成 → 解析动画；整体节奏不短于点击后 1.8s（与原型一致）
    parseDesc.value = "正在解压案件材料";
    const rest = Math.max(0, 1800 - (Date.now() - startedAt));
    parseTimers.push(
      setTimeout(() => (parsePct.value = 62), Math.min(650, rest)),
    );
    parseTimers.push(
      setTimeout(() => {
        parseActive.value = false;
        // 缓存解析会话：弹窗关闭后可免重传重开（见 parsedSession 注释）
        parsedSession.value = {
          docIds: [...docIds.value],
          config: dossierConfig.value,
          snapshot,
        };
        caseInfoOpen.value = true;
      }, rest),
    );
  } catch (err) {
    // 上传失败：收起进度弹窗、保留已选材料便于重试。
    // 业务 / 状态码错误 http.js 已 toast；此处兜底网络类静默失败（如后端未启动）
    parseActive.value = false;
    console.error("[JudicativePaper] importDossier error:", err);
    message.error("案件材料上传失败，请稍后重试");
  }
}

// 案件信息表单「提交生成」：调 GenerationJudgment 后先刷新列表（行内「生成中」）；
// 请求 settle 后：成功提示完成；400 缺材料则弹 TaskInfoModal，引导补充材料
function submitCaseGenerate(payload) {
  const docId = docIds.value.find(Boolean);
  if (!docId) {
    message.error("缺少文书标识，请重新上传解析");
    return;
  }
  // reasoning：首页补充说明（案件情况 / 裁判思路），清空输入区前先取值；
  // 空视为未填写，不传（仅用户真实输入才传后端）；
  // caseType：案件类型（案件信息表单提交，默认「民事一审」）
  const reasoning = requirementText.value.trim() || undefined;
  const docTypeLabel = activeType.value;
  caseInfoOpen.value = false;
  files.value = [];
  requirementText.value = "";
  parsedSession.value = null; // 会话已消费，材料区清空回到初始态
  message.success("已提交文书生成");
  fetchRecentTasks();
  generationJudgment({
    docId,
    mode: payload.mode,
    caseReason: payload.caseReason,
    applicableProcedure: payload.applicableProcedure,
    reasoning,
    caseType: payload.caseType,
    // 文书子类型：首页下拉选中项对应的枚举 value（字符串，判决书一般为 "1"）
    documentSubType: String(docTypeValue(docTypeLabel)),
  })
    .then(() => {
      message.success("文书生成完成");
    })
    .catch((err) => {
      console.error("[JudicativePaper] generationJudgment error:", err);
      if (isGenerateMissingError(err)) {
        // 全局拦截器已 toast 过，再弹缺材料窗引导补充
        openMissingFromGenerateError({
          docId,
          type: docTypeLabel,
          name: "裁判文书",
          errMsg: err?.message,
        });
        return;
      }
      // 业务 400 已由 http 拦截器 toast；此处仅兜底无有效文案的网络类失败
      const msg = err?.message;
      if (!msg || msg === "undefined" || msg === "Error") {
        message.error("文书生成提交失败，请稍后重试");
      }
    })
    .finally(() => {
      fetchRecentTasks(true);
    });
}
</script>

<style scoped>
/* 变量挂在页面根节点（scoped 下 :root 不可用） */
.judgment-home {
  --brand: #1768ff;
  --brand-hover: #0e59db;
  --brand-soft: #eef4ff;
  --ink: #17243a;
  --line: #e5ebf3;
  --line-2: #f0f3f7;
  --shadow: 0 8px 26px rgba(31, 53, 88, 0.06);
  --radius: 16px;
  width: 100%;
  margin-top: 24px;
  color: var(--ink);
}

/* ---------- 通用 ---------- */
button {
  cursor: pointer;
}
.icon {
  width: 20px;
  height: 20px;
  stroke: currentColor;
  fill: none;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.icon.sm {
  width: 16px;
  height: 16px;
}

.card {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}
.btn {
  height: 40px;
  border: 1px solid #dce4f0;
  border-radius: 10px;
  background: #fff;
  color: #31435f;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 650;
  font-size: 14px;
  transition: 0.16s;
}
.btn:hover {
  background: #f8fbff;
  border-color: #c9d6e8;
}
.btn.primary {
  background: var(--brand);
  border-color: var(--brand);
  color: #fff;
}
.btn.primary:hover {
  background: var(--brand-hover);
}
.btn:disabled {
  background: #f4f6f9 !important;
  border-color: #e1e6ee !important;
  color: #a1adbd !important;
  cursor: not-allowed !important;
  box-shadow: none !important;
}
.btn.primary:disabled {
  background: #a9c5f8 !important;
  border-color: #a9c5f8 !important;
  color: #fff !important;
}
.icon-btn {
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  border-radius: 8px;
  color: #60718a;
  display: grid;
  place-items: center;
}
.icon-btn:hover {
  background: #eef4ff;
  color: var(--brand);
}

/* ---------- 首页 ---------- */
.page-wrap {
  max-width: 1080px;
  margin: 0 auto;
  padding-bottom: 24px;
}
.page-title {
  margin: 0;
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.5px;
}
.page-subtitle {
  margin-top: 10px;
  color: #65758e;
  font-size: 15px;
  line-height: 1.7;
}

.composer {
  margin-top: 22px;
  min-height: 286px;
  padding: 18px 24px 16px;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: border-color 0.14s, box-shadow 0.14s, background 0.14s;
}
.composer.dragover {
  border-color: var(--brand);
  background: #f7faff;
  box-shadow: 0 0 0 3px rgba(23, 104, 255, 0.08), var(--shadow);
}
.composer-files {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.attachment {
  max-width: 330px;
  min-width: 235px;
  height: 58px;
  border: 1px solid #dfe7f2;
  border-radius: 11px;
  background: #fbfdff;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 8px 8px 12px;
}
.attachment-ico {
  width: 40px;
  height: 44px;
  display: grid;
  place-items: center;
  flex: none;
}
.attachment-main {
  min-width: 0;
  flex: 1;
}
.attachment-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  font-weight: 700;
  color: #31435e;
}
.attachment-meta {
  margin-top: 3px;
  color: #8a97aa;
  font-size: 11px;
}
.attachment .icon-btn {
  width: 28px;
  height: 28px;
  flex: none;
}
.composer-textarea {
  width: 100%;
  min-height: 78px;
  flex: 0 0 auto;
  resize: none;
  border: 0;
  outline: 0;
  padding: 0 2px 8px;
  color: #263b58;
  font-size: 15px;
  line-height: 1.75;
  background: transparent;
}
.composer-textarea::placeholder {
  color: #8092af;
}
.composer-footer {
  margin-top: auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 18px;
  padding-top: 8px;
}
.composer-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
}
.start-wrap {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  min-height: 40px;
}
.start-tip {
  font-size: 11px;
  color: #8a97aa;
  white-space: nowrap;
}

/* 文书类型下拉 */
.doc-type-control {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.doc-type-trigger {
  height: 40px;
  min-width: 96px;
  max-width: 180px;
  border: 1px solid #e3e7ee;
  border-radius: 10px;
  background: #f4f5f7;
  color: #263a56;
  padding: 0 11px 0 13px;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 7px;
  outline: 0;
  font-size: 13px;
  font-weight: 650;
  cursor: pointer;
  white-space: nowrap;
  transition: 0.16s;
}
.doc-type-trigger:hover {
  background: #eef2f7;
  border-color: #d9e0e9;
}
.doc-type-trigger.open {
  background: #fff;
  border-color: #b8cef3;
  box-shadow: 0 0 0 3px rgba(23, 104, 255, 0.06);
}
.doc-type-trigger .doc-type-caret {
  width: 16px;
  height: 16px;
  color: #5f6f85;
  stroke-width: 2;
  transition: transform 0.18s;
  flex: none;
}
.doc-type-trigger.open .doc-type-caret {
  transform: rotate(180deg);
}
.doc-type-menu {
  position: absolute;
  left: 0;
  bottom: 48px;
  width: 220px;
  padding: 7px;
  background: #fff;
  border: 1px solid #e2e7ee;
  border-radius: 14px;
  box-shadow: 0 14px 38px rgba(28, 45, 72, 0.16);
  z-index: 80;
  opacity: 0;
  visibility: hidden;
  transform: translateY(6px) scale(0.985);
  transform-origin: left bottom;
  transition: opacity 0.14s, transform 0.14s, visibility 0.14s;
}
.doc-type-menu.show {
  opacity: 1;
  visibility: visible;
  transform: translateY(0) scale(1);
}
.doc-type-option {
  width: 100%;
  height: 44px;
  border: 0;
  border-radius: 9px;
  background: #fff;
  color: #24364f;
  padding: 0 11px;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  font-size: 13px;
  font-weight: 560;
  transition: 0.14s;
}
.doc-type-option:hover:not(:disabled) {
  background: #f5f7fa;
}
.doc-type-option .option-icon {
  width: 18px;
  height: 18px;
  color: #33465f;
  flex: none;
}
.doc-type-option .option-label {
  flex: 1;
  white-space: nowrap;
}
.doc-type-option .option-check {
  width: 18px;
  height: 18px;
  color: #17243a;
  opacity: 0;
}
.doc-type-option.active {
  font-weight: 680;
  background: #f8faff;
}
.doc-type-option.active .option-check {
  opacity: 1;
}
.doc-type-option:disabled,
.doc-type-option.disabled {
  color: #9aa8bc;
  cursor: not-allowed;
  background: #fff;
}
.doc-type-option:disabled .option-icon,
.doc-type-option.disabled .option-icon {
  color: #b7c2d1;
}

/* 最近文书 */
.section-headline {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin: 38px 0 16px;
}
.section-headline h2 {
  font-size: 19px;
  margin: 0;
  font-weight: 800;
}
.section-sub {
  font-size: 12px;
  color: #8492a6;
  margin-top: 6px;
}
.all-record-link {
  height: 32px;
  border: 0;
  background: transparent;
  color: var(--brand);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 6px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
}
.all-record-link:hover {
  background: var(--brand-soft);
}
.home-tabs {
  margin-bottom: 18px;
}
.record-card {
  overflow: hidden;
}
.records-loading,
.records-empty {
  padding: 48px;
  display: grid;
  place-items: center;
}
</style>
